import React, { useEffect, useRef } from "react";
import { getCaps } from "@/studio/lib/perf";

/**
 * Atmosphere — a quiet field of stars on the dark canvas.
 *
 * Direction (Feb 2026, final creative pass):
 *  • 150 stars max on desktop, scaled down on lower tiers
 *  • 95% are perfectly static, slightly brighter than before
 *  • ~5% twinkle on a random ~10–15s cadence (one brief fade per star)
 *  • No dust, no parallax, no fog leaks, no movement
 *  • Honours reduced-motion (renders once, never updates)
 *  • Pauses when the tab is hidden
 *  • Opacity gated by --atmosphere (0 light → 1 dark)
 */
export default function Atmosphere() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const caps = getCaps();
    const dpr = Math.min(window.devicePixelRatio || 1, caps.isMobile ? 1.5 : 2);
    const reduced = caps.reduced;

    const starCount = caps.isMobile ? 70 : caps.lowPower ? 110 : 150;
    const twinkleRatio = 0.05; // 5%
    const twinkleEvery = [10000, 15000]; // ms range between twinkle bursts per star

    let w = 0;
    let h = 0;
    let stars = [];
    let raf = 0;
    let running = false;
    let lastDraw = 0;

    const resize = () => {
      w = canvas.width = window.innerWidth * dpr;
      h = canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };

    const spawnStar = (i) => {
      const isTwinkler = Math.random() < twinkleRatio;
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        r: (Math.random() * 0.9 + 0.35) * dpr,
        // slightly brighter base than before (was 0.2-0.8 → now 0.45-0.95)
        a: Math.random() * 0.5 + 0.45,
        twinkler: isTwinkler,
        // randomise first twinkle so they don't all blink at once
        nextTwinkleAt:
          performance.now() +
          twinkleEvery[0] +
          Math.random() * (twinkleEvery[1] - twinkleEvery[0]),
        twinkleStart: 0, // 0 = idle, >0 = currently twinkling
      };
    };
    const init = () => {
      stars = Array.from({ length: starCount }, spawnStar);
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        ctx.fillStyle = `rgba(255,255,255,${s.a.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const TWINKLE_DURATION = 1400; // ms — single soft fade-in-out

    const draw = (t) => {
      // Throttle to ~6fps — nothing moves, we only need to repaint when
      // a star is mid-twinkle.
      if (t - lastDraw < 160) {
        if (running) raf = requestAnimationFrame(draw);
        return;
      }
      lastDraw = t;

      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        let alpha = s.a;
        if (s.twinkler) {
          if (s.twinkleStart === 0 && t >= s.nextTwinkleAt) {
            s.twinkleStart = t;
          }
          if (s.twinkleStart > 0) {
            const k = (t - s.twinkleStart) / TWINKLE_DURATION;
            if (k >= 1) {
              s.twinkleStart = 0;
              s.nextTwinkleAt =
                t +
                twinkleEvery[0] +
                Math.random() * (twinkleEvery[1] - twinkleEvery[0]);
            } else {
              // ease in-out then back: 0 → 1 → 0
              const e = Math.sin(k * Math.PI);
              alpha = Math.min(1, s.a + e * 0.55);
            }
          }
        }
        ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }

      if (running) raf = requestAnimationFrame(draw);
    };

    const start = () => {
      if (running || reduced || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(draw);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    resize();
    init();
    if (reduced) drawStatic();
    else start();

    const onResize = () => {
      resize();
      init();
      if (reduced) drawStatic();
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div className="studio-atmosphere" aria-hidden="true">
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
}
